import {
  ConfirmDialog,
  SelectInput,
  SubmitButton,
  TextInput,
  useSnackbarContext,
} from "@ameelio/ui";
import { useMutation, useQuery } from "@apollo/client/react";
import { Alert, AlertTitle, Box, Grid, Stack, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  OrganizationMembershipStatus,
  OrganizationType,
  UsState,
} from "../api/graphql";
import { ContentSkeleton } from "../lib/closet";
import { AvailableCountry, getCountryLabel } from "../lib/DocumentType";
import useApolloErrorHandler from "../lib/handleApolloError";
import ImageInput from "../lib/ImageInput";
import organizationTypeLabel from "../lib/organizationTypeLabel";
import Screen from "../lib/Screen";
import ScreenSection from "../lib/ScreenSection";
import useFileUpload from "../lib/useFileUpload";
import USState from "../lib/USState.enum";
import {
  isEmail,
  isNotRequired,
  isPhoneNumber,
  isRequired,
  isValidUrl,
  MAX_PHONE_NUMBER_LENGTH,
  mergeRules,
} from "../lib/validate";
import { useCurrentVisitor } from "../SessionBoundary";
import { CreateOrganizationDocument } from "./CreateOrganization.generated";
import { GetInviteOrganizationByUserDocument } from "./GetInviteOrganizationByUser.generated";

type FormData = {
  name: string;
  type: OrganizationType;
  description: string;
  websiteLink?: string;
  addressLineOne: string;
  addressLineTwo?: string;
  city: string;
  state: UsState;
  province: string;
  country: string;
  postal: string;
  phone: string;
  email: string;
  logoImage: File;
};

const ensureURLProtocol = (url?: string) =>
  url && !url.match(/^http(s)?:\/\//) ? `https://${url}` : url;

type ConfirmCreationDialogProps = {
  onCancel: () => void;
  onConfirm: () => void;
};

const ConfirmCreationDialog = ({
  onCancel,
  onConfirm,
}: ConfirmCreationDialogProps) => {
  const { t } = useTranslation();
  return (
    <ConfirmDialog
      onConfirm={onConfirm}
      onCancel={onCancel}
      description={t(
        "Ameelio organizations are not for family or personal communication. Please confirm you work professionally with incarcerated people and intend to use your Ameelio Connect account for that work."
      )}
      confirmText={t("Yes, create organization")}
      variant="remove"
    />
  );
};

export default function NewOrganizationScreen() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const handleApolloError = useApolloErrorHandler();
  const currentVisitor = useCurrentVisitor();
  const snackbarContext = useSnackbarContext();
  const [creating, setCreating] = useState(false);
  const [pendingFormData, setPendingFormData] = useState<FormData | null>(null);

  const confirmOpen = !!pendingFormData;

  useEffect(() => {
    // If the user is already a member of an organization, redirect them to the team page
    if (currentVisitor.organizationMemberships.length > 0) {
      const membership = currentVisitor.organizationMemberships.find(
        (r) => r.status === OrganizationMembershipStatus.Active
      );
      if (membership) {
        navigate(`/organization/${membership.organization.id}/team`);
      }
    }
  }, [currentVisitor, navigate]);

  const {
    control,
    handleSubmit,
    watch,
    formState: { isSubmitting },
  } = useForm<FormData>({
    mode: "onTouched",
    defaultValues: {
      country: AvailableCountry.US,
    },
  });

  const [createOrganization] = useMutation(CreateOrganizationDocument, {
    onError: (e) => {
      setCreating(false);
      handleApolloError(e);
    },
    onCompleted: () => {
      setCreating(false);
    },
  });

  const { data: orgInvitationData, loading } = useQuery(
    GetInviteOrganizationByUserDocument
  );

  useEffect(() => {
    if (orgInvitationData && orgInvitationData.organizationInvitationByUser) {
      navigate("/");
      snackbarContext.alert(
        "warning",
        t(
          "You already have a pending invitation for an organization. Please contact the organization administrator for an updated invitation email."
        )
      );
    }
  }, [orgInvitationData, navigate, snackbarContext, t]);

  const { upload } = useFileUpload();

  const countryValue = watch("country");

  const inferProvince = (formData: FormData) =>
    formData.country === AvailableCountry.US
      ? { province: USState[formData.state] }
      : { province: formData.province, state: UsState.Ak };

  const snackbar = useSnackbarContext();

  const onSubmit = async (data: FormData) => {
    const { logoImage, ...remainingData } = data;
    let logoImageKey: string | undefined;
    if (logoImage) logoImageKey = await upload(logoImage);
    setCreating(true);
    await createOrganization({
      variables: {
        input: {
          ...remainingData,
          ...(logoImageKey ? { logoImageKey } : {}),
          ...inferProvince(data),
          websiteLink: ensureURLProtocol(data.websiteLink) || undefined,
        },
      },
      onCompleted: () => {
        setCreating(false);
        snackbar.alert(
          "success",
          t(
            "Organization created successfully. You may now request professional contacts."
          )
        );
        navigate(`/`);
      },
    });
  };

  const onFormSubmit = (data: FormData) => {
    setPendingFormData(data); // Store the validated data
  };

  const handleConfirm = async () => {
    if (pendingFormData) {
      await onSubmit(pendingFormData);
      setPendingFormData(null);
    }
  };

  const handleCancel = () => {
    setPendingFormData(null);
  };

  return loading ? (
    <ContentSkeleton />
  ) : (
    <>
      {confirmOpen && (
        <ConfirmCreationDialog
          onCancel={handleCancel}
          onConfirm={handleConfirm}
        />
      )}
      <Screen title={t("Create professional organization")}>
        <form onSubmit={handleSubmit(onFormSubmit)}>
          <div>
            <div>
              <Alert
                severity="info"
                variant="outlined"
                sx={{
                  mt: 1,
                  alignItems: "flex-start",
                  backgroundColor: "white",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                  }}
                >
                  <AlertTitle>
                    {t("Do you work with incarcerated people?")}
                  </AlertTitle>
                  <Typography variant="body2">
                    {t(
                      "Add organization details to your Ameelio Connect account to unlock additional features."
                    )}
                  </Typography>
                </Box>
              </Alert>
              <Grid
                sx={{
                  height: 1,
                  overflowY: "auto",
                }}
              >
                <ScreenSection title={t("Details")}>
                  <Stack
                    spacing={2}
                    sx={{
                      pb: 2,
                    }}
                  >
                    <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                      <Box
                        sx={{
                          width: "128px",
                          height: "128px",
                        }}
                      >
                        <ImageInput
                          control={control}
                          name="logoImage"
                          rules={isNotRequired()}
                          primaryLabel={t("Click or drag to upload logo")}
                          hideFormatLabel
                          round
                        />
                      </Box>
                      <Stack
                        spacing={2}
                        sx={{
                          flex: "1",
                        }}
                      >
                        <TextInput
                          control={control}
                          name="name"
                          label={t("Organization name")}
                          rules={isRequired(t("Please enter a valid name."))}
                        />
                        <SelectInput
                          control={control}
                          name="type"
                          label={t("Organization type")}
                          rules={isRequired(
                            t("Please select an organization type.")
                          )}
                          items={Object.values(OrganizationType).map((v) => ({
                            value: v,
                            name: organizationTypeLabel(v),
                          }))}
                        />
                      </Stack>
                    </Stack>
                    <TextInput
                      control={control}
                      name="description"
                      label={t("Organization description")}
                      rules={isRequired(t("Please enter a description."))}
                      multiline
                      minRows={5}
                      maxRows={10}
                    />
                  </Stack>
                </ScreenSection>
                <ScreenSection title={t("Organization address")}>
                  <Stack
                    spacing={2}
                    sx={{
                      pb: 2,
                    }}
                  >
                    <TextInput
                      control={control}
                      name="addressLineOne"
                      label={t("Line One")}
                      rules={isRequired(t("Please enter an address."))}
                    />
                    <TextInput
                      control={control}
                      name="addressLineTwo"
                      label={t("Line Two")}
                      rules={isNotRequired()}
                    />
                    <TextInput
                      control={control}
                      name="city"
                      label={t("City")}
                      rules={isRequired(t("Please enter a city."))}
                    />
                    <SelectInput
                      items={Object.keys(AvailableCountry).map((v) => ({
                        value: v,
                        name: getCountryLabel(v as AvailableCountry),
                      }))}
                      name="country"
                      label={t("Country")}
                      control={control}
                      rules={isRequired(t("Please enter a country code."))}
                    />
                    {countryValue === AvailableCountry.US ? (
                      <SelectInput
                        control={control}
                        name="state"
                        label={t("State")}
                        rules={isRequired(t("Please select a valid US state."))}
                        items={Object.values(UsState).map((v) => ({
                          value: v,
                          name: USState[v],
                        }))}
                      />
                    ) : (
                      <TextInput
                        control={control}
                        name="province"
                        label={t("Province")}
                        rules={isRequired(t("Please enter a province."))}
                      />
                    )}
                    <TextInput
                      control={control}
                      name="postal"
                      label={
                        countryValue === AvailableCountry.US
                          ? t("ZIP code")
                          : t("Postal code")
                      }
                      rules={isRequired(
                        countryValue === AvailableCountry.US
                          ? t("Please enter a valid ZIP code.")
                          : t("Please enter a valid postal code.")
                      )}
                    />
                  </Stack>
                </ScreenSection>
                <ScreenSection title={t("Primary contact")}>
                  <Stack
                    spacing={2}
                    sx={{
                      pb: 2,
                    }}
                  >
                    <TextInput
                      control={control}
                      name="phone"
                      label={t("Phone number")}
                      slotProps={{
                        htmlInput: { maxLength: MAX_PHONE_NUMBER_LENGTH },
                      }}
                      rules={mergeRules(
                        isRequired(t("Please enter your phone number.")),
                        isPhoneNumber()
                      )}
                    />
                    <TextInput
                      control={control}
                      name="email"
                      label={t("Email Address")}
                      rules={mergeRules(
                        isRequired(t("Please enter a valid email address.")),
                        isEmail()
                      )}
                    />
                    <TextInput
                      control={control}
                      helperText={t(
                        "If included, must start with https:// or no protocol."
                      )}
                      name="websiteLink"
                      label={t("Website URL")}
                      rules={mergeRules(isNotRequired(), isValidUrl())}
                    />
                  </Stack>
                </ScreenSection>
                <Box
                  sx={{
                    mt: 2,
                    display: "flex",
                    justifyContent: "flex-end",
                  }}
                >
                  <SubmitButton
                    submitting={isSubmitting || creating}
                    sx={{ width: "auto" }}
                  >
                    {t("Submit")}
                  </SubmitButton>
                </Box>
              </Grid>
            </div>
          </div>
        </form>
      </Screen>
    </>
  );
}
